import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, User } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface Student {
  id: string;
  name: string;
}

interface StudentSelectorProps {
  students: Student[];
  selectedStudentId: string | null;
  onSelect: (studentId: string) => void;
  label?: string;
  error?: string;
  placeholder?: string;
  className?: string;
  dropdownClassName?: string;
  showLabel?: boolean;
}

export function StudentSelector({
  students,
  selectedStudentId,
  onSelect,
  label,
  error,
  placeholder = 'Select a student',
  className = '',
  dropdownClassName = '',
  showLabel = true
}: StudentSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedStudent = students.find(s => s.id === selectedStudentId);
  const selectedStudentName = selectedStudent?.name || '';

  // Show search only if there are more than 3 students
  const showSearch = students.length > 3;

  // Filter students based on search text (only if search is shown)
  const filteredStudents = showSearch
    ? students.filter(student =>
        student.name.toLowerCase().includes(searchText.toLowerCase())
      )
    : students;

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchText('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (studentId: string) => {
    onSelect(studentId);
    setIsOpen(false);
    setSearchText('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {showLabel && label && (
        <Label htmlFor="studentSelector">{label}</Label>
      )}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 px-3 py-2 bg-white border rounded-lg hover:bg-gray-50 transition-colors w-full ${
          error ? 'border-destructive-action' : 'border-gray-300'
        }`}
      >
        <User className="w-4 h-4 text-gray-500 hidden sm:block" />
        <span className="text-sm font-medium text-gray-700 flex-1 text-left">
          {selectedStudentName || placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {error && <p className="text-sm text-destructive-action mt-1">{error}</p>}
      
      {isOpen && (
        <div className={`absolute z-50 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden flex flex-col ${dropdownClassName}`}>
          {showSearch && (
            <div className="p-2 border-b border-gray-200">
              <Input
                placeholder="Search students..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full"
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          )}
          <div className="overflow-y-auto py-1">
            {filteredStudents.length > 0 ? (
              filteredStudents.map((student) => (
                <button
                  key={student.id}
                  type="button"
                  onClick={() => handleSelect(student.id)}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${
                    student.id === selectedStudentId ? 'bg-action-blue/10 text-action-blue-dark' : 'text-gray-700'
                  }`}
                >
                  {student.name}
                </button>
              ))
            ) : (
              <div className="px-4 py-2 text-sm text-gray-500">No students found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}




